/**
 * The few facts a spectator (someone with no team to follow) is shown at the
 * top of Home. Each is a pure lookup over the profiles or the snapshot; the
 * current leader needs none, it is `LeagueSnapshot.standings[0]`.
 */

import type { LeagueSnapshot, SlateGame } from "./leagueSnapshot";
import type { ManagerProfile } from "./managerProfile";

export interface ReigningChampion {
  personId: string;
  season: number;
}

/** Whoever won the most recent title; null while nobody has won one. */
export function reigningChampion(profiles: readonly ManagerProfile[]): ReigningChampion | null {
  let latest: ReigningChampion | null = null;
  for (const profile of profiles) {
    const season = profile.titles.at(-1); // oldest first
    if (season !== undefined && (latest === null || season > latest.season)) latest = { personId: profile.personId, season };
  }
  return latest;
}

/**
 * The week's most interesting game by standings alone: the one whose two teams
 * rank best together. Consolation games are skipped; on a tie the earlier game
 * in the slate wins. Null once the season is over (no slate).
 */
export function gameToWatch(snapshot: LeagueSnapshot): SlateGame | null {
  const rankOf = new Map(snapshot.standings.map((row) => [row.personId, row.rank]));
  const worst = snapshot.standings.length + 1; // a team missing from the standings counts as last
  const combinedRank = (game: SlateGame) => game.personIds.reduce((sum, id) => sum + (rankOf.get(id) ?? worst), 0);

  let best: SlateGame | null = null;
  for (const game of snapshot.slate) {
    if (game.gameType === "consolation") continue;
    if (best === null || combinedRank(game) < combinedRank(best)) best = game;
  }
  return best;
}
